import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GraduationCap, Plus, Search, Edit, Trash2, Users, BookOpen, Award, Share2, Copy } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { createApiUrl } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Teacher {
  id: string;
  name: string;
  phone: string;
  email: string;
  role: string;
  subject: string;
  otp_verified: boolean;
  created_at: string;
}

const AdminTeachers = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [newTeacher, setNewTeacher] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      setIsLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        toast({
          title: "Error",
          description: "Please login again",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch(createApiUrl('/admin/teachers?skip=0&limit=10'), {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTeachers(data.teachers || []);
      } else if (response.status === 401) {
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to fetch teachers",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast({
        title: "Error",
        description: "Failed to connect to server",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTeachers = teachers.filter(teacher =>
    teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    teacher.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddTeacher = () => {
    if (!newTeacher.name || !newTeacher.email || !newTeacher.phone || !newTeacher.subject) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    // For now, just add locally - in real app this would POST to API
    const teacher: Teacher = {
      id: Date.now().toString(),
      name: newTeacher.name,
      email: newTeacher.email,
      phone: newTeacher.phone,
      subject: newTeacher.subject,
      role: "",
      otp_verified: false,
      created_at: new Date().toISOString()
    };

    setTeachers([...teachers, teacher]);
    toast({
      title: "Teacher Added!",
      description: `${teacher.name} has been added successfully`
    });

    setNewTeacher({ name: "", email: "", phone: "", subject: "" });
    setIsAddDialogOpen(false);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading teachers...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-red-500 to-pink-600 rounded-2xl p-4 sm:p-6 lg:p-8 text-white shadow-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 sm:gap-3 mb-2">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8" />
                </div>
                <span className="hidden sm:inline">Teachers Management</span>
                <span className="sm:hidden">Teachers</span>
              </h1>
              <p className="text-red-100 text-base sm:text-lg">Manage teacher accounts and assignments</p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 mt-3 sm:mt-4">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-red-200" />
                  <span className="text-red-100 text-sm sm:text-base">{teachers.length} Active Teachers</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-red-200" />
                  <span className="text-red-100 text-sm sm:text-base">All Subjects Covered</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={() => {
                  const inviteLink = `${window.location.origin}/teacher/register`;
                  navigator.clipboard.writeText(inviteLink);
                  toast({
                    title: "Invite Link Copied!",
                    description: "Share this link with teachers to register"
                  });
                }}
                className="bg-white/20 text-white border-white/20 hover:bg-white/30 shadow-xl hover:shadow-2xl transition-all duration-200 h-10 sm:h-12 px-4 sm:px-6 font-semibold text-sm sm:text-base"
                variant="outline"
              >
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                <span className="hidden sm:inline">Share Invite Link</span>
                <span className="sm:hidden">Share Link</span>
              </Button>

              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-red-600 hover:bg-red-50 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 h-10 sm:h-12 px-4 sm:px-6 font-semibold text-sm sm:text-base">
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span className="hidden sm:inline">Add Manually</span>
                    <span className="sm:hidden">Add Manual</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="mx-4 w-full max-w-lg sm:mx-auto">
                  <DialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl">
                        <GraduationCap className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <DialogTitle className="text-xl font-bold">Add New Teacher</DialogTitle>
                        <p className="text-sm text-gray-600">Create a new teacher account</p>
                      </div>
                    </div>
                  </DialogHeader>
                  <div className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
                        <Input
                          id="name"
                          value={newTeacher.name}
                          onChange={(e) => setNewTeacher({...newTeacher, name: e.target.value})}
                          placeholder="Enter teacher's full name"
                          className="h-11 border-gray-200 focus:border-red-500 focus:ring-red-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={newTeacher.email}
                          onChange={(e) => setNewTeacher({...newTeacher, email: e.target.value})}
                          placeholder="teacher@email.com"
                          className="h-11 border-gray-200 focus:border-red-500 focus:ring-red-500"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={newTeacher.phone}
                        onChange={(e) => setNewTeacher({...newTeacher, phone: e.target.value})}
                        placeholder="+91 98765 43210"
                        className="h-11 border-gray-200 focus:border-red-500 focus:ring-red-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-gray-700 font-medium">Subject *</Label>
                      <Select value={newTeacher.subject} onValueChange={(value) => setNewTeacher({...newTeacher, subject: value})}>
                        <SelectTrigger className="h-11 border-gray-200 focus:border-red-500 focus:ring-red-500">
                          <SelectValue placeholder="Select subject to teach" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="General">📚 General</SelectItem>
                          <SelectItem value="Mathematics">📐 Mathematics</SelectItem>
                          <SelectItem value="Physics">⚡ Physics</SelectItem>
                          <SelectItem value="Chemistry">🧪 Chemistry</SelectItem>
                          <SelectItem value="Biology">🌱 Biology</SelectItem>
                          <SelectItem value="English">📚 English</SelectItem>
                          <SelectItem value="Hindi">🇮🇳 Hindi</SelectItem>
                          <SelectItem value="History">📜 History</SelectItem>
                          <SelectItem value="Geography">🌍 Geography</SelectItem>
                          <SelectItem value="Computer Science">💻 Computer Science</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleAddTeacher} className="w-full h-12 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200">
                      <Plus className="w-5 h-5 mr-2" />
                      Add Teacher
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>

        <Card className="shadow-xl border-0 overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Award className="w-6 h-6 text-red-600" />
                  All Teachers ({teachers.length})
                </CardTitle>
                <p className="text-gray-600 mt-1">View and manage all registered teachers</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search teachers by name, email, or subject..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500 rounded-xl shadow-sm"
              />
            </div>

            {/* Mobile Card View */}
            <div className="block sm:hidden space-y-4">
              {filteredTeachers.map((teacher) => (
                <Card key={teacher.id} className="p-4 border border-gray-200 rounded-xl hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-semibold text-sm">
                        {teacher.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{teacher.name}</h3>
                          <p className="text-xs text-gray-500 mt-1">{teacher.subject}</p>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0 hover:bg-blue-50">
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0 hover:bg-red-50 text-red-600">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-600 truncate">{teacher.email}</p>
                        <p className="text-xs text-gray-500">{teacher.phone}</p>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden sm:block rounded-xl border border-gray-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-gray-50">
                    <TableHead className="font-semibold text-gray-700">Teacher</TableHead>
                    <TableHead className="font-semibold text-gray-700">Subject</TableHead>
                    <TableHead className="font-semibold text-gray-700 hidden md:table-cell">Contact</TableHead>
                    <TableHead className="font-semibold text-gray-700 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTeachers.map((teacher) => (
                    <TableRow key={teacher.id} className="hover:bg-gray-50 transition-colors">
                      <TableCell>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-xs sm:text-sm">
                              {teacher.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-sm sm:text-base">{teacher.name}</div>
                            <div className="text-xs sm:text-sm text-gray-500">ID: {teacher.id.substring(0, 8)}...</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 sm:px-3 bg-purple-100 text-purple-800 rounded-full text-xs sm:text-sm font-medium">
                          {teacher.subject}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-900 truncate max-w-32">{teacher.email}</div>
                          <div className="text-sm text-gray-500">{teacher.phone}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1 sm:gap-2">
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0 sm:h-auto sm:w-auto sm:px-2 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700">
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 w-8 p-0 sm:h-auto sm:w-auto sm:px-2 hover:bg-red-50 hover:border-red-300 text-red-600 hover:text-red-700">
                            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminTeachers;
